import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, Zap, Lock, Globe, Cpu, Bell } from "lucide-react";

const toAlignClass = (a) => {
  const v = String(a || "").toLowerCase().trim();
  if (v === "center") return "text-center";
  if (v === "right") return "text-right";
  if (v === "left") return "text-left";
  return "";
};

const buildBg = (style, defaultClass) => {
  const mode = style?.bgMode || "default";

  if (mode === "solid" && style?.bgColor) {
    return { style: { backgroundColor: style.bgColor }, className: "" };
  }

  if (mode === "gradient") {
    const from = (style?.bgGradientFrom || "").trim();
    const via = (style?.bgGradientVia || "").trim();
    const to = (style?.bgGradientTo || "").trim();
    const angleNum = Number(style?.bgGradientAngle);
    const angle = Number.isFinite(angleNum) ? angleNum : 180;

    if (from && to) {
      const stops = via ? `${from}, ${via}, ${to}` : `${from}, ${to}`;
      return {
        style: { backgroundImage: `linear-gradient(${angle}deg, ${stops})` },
        className: "",
      };
    }
  }

  return { style: undefined, className: defaultClass };
};

const isBlank = (v) => (v ?? "").toString().trim().length === 0;

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const hexToRgba = (hex, alpha = 1) => {
  const h = (hex || "").replace("#", "").trim();
  const a = Number.isFinite(Number(alpha)) ? clamp(Number(alpha), 0, 1) : 1;

  if (h.length !== 6) return `rgba(255,255,255,${a})`;

  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);

  if ([r, g, b].some((x) => Number.isNaN(x))) return `rgba(255,255,255,${a})`;
  return `rgba(${r},${g},${b},${a})`;
};

const gradientStyle = (from, to, angle = 135) => {
  const f = (from || "").trim();
  const t = (to || "").trim();
  if (!f || !t) return undefined;
  return { backgroundImage: `linear-gradient(${angle}deg, ${f}, ${t})` };
};

const FeaturesSection3 = ({ config = {}, style }) => {
  const [activeTab, setActiveTab] = useState(0);

  const features = Array.isArray(config.features) ? config.features : [];

  // keep activeTab valid
  useEffect(() => {
    if (!features.length) return;
    setActiveTab((prev) => clamp(prev, 0, features.length - 1));
  }, [features.length]);

  const getIcon = (iconName) => {
    const icons = {
      "📊": <BarChart3 className="w-6 h-6" />,
      "🚀": <Zap className="w-6 h-6" />,
      "🔒": <Lock className="w-6 h-6" />,
      "🌐": <Globe className="w-6 h-6" />,
      "🤖": <Cpu className="w-6 h-6" />,
      "🔔": <Bell className="w-6 h-6" />,
      default: <Zap className="w-6 h-6" />,
    };
    return icons[iconName] || icons.default;
  };

  const bg = buildBg(style, "bg-white");

  const align = (style?.textAlign || "").trim();
  const hasAlign = align.length > 0;

  // keep old behavior when not set
  const headerAlignClass = hasAlign ? toAlignClass(align) : "text-start";

  const activeFeature = useMemo(() => {
    if (!features.length) return null;
    return features[clamp(activeTab, 0, features.length - 1)] || null;
  }, [features, activeTab]);

  // Feature icon gradient:
  // supports new: accentFrom/accentTo (color pickers)
  // supports old: color = "from-blue-500 to-purple-600" (Tailwind)
  const featureIconBox = (f) => {
    const from = f?.accentFrom;
    const to = f?.accentTo;
    if (!isBlank(from) && !isBlank(to)) {
      return { style: gradientStyle(from, to, 135), className: "" };
    }
    const tw = (f?.color || "").trim();
    if (tw) return { style: undefined, className: `bg-gradient-to-br ${tw}` };
    return { style: gradientStyle("#3b82f6", "#a855f7", 135), className: "" };
  };

  // Right panel config (editable)
  const panel = config.panel || {};
  const panelBgFrom = panel.bgFrom || "#111827";
  const panelBgTo = panel.bgTo || "#000000";
  const panelTextColor = panel.textColor || "#ffffff";
  const panelMutedColor = panel.mutedTextColor || "#d1d5db";

  const statCardHex = panel.statCardBg || "#ffffff";
  const statCardOpacity = Number.isFinite(Number(panel.statCardOpacity))
    ? clamp(Number(panel.statCardOpacity), 0, 1)
    : 0.1;

  const stats =
    Array.isArray(panel.stats) && panel.stats.length
      ? panel.stats
      : [
          { value: "99.9%", label: "Uptime" },
          { value: "256-bit", label: "Encryption" },
          { value: "24/7", label: "Support" },
          { value: "100+", label: "Integrations" },
        ];

  const previewEmoji = panel.previewEmoji || "📈";
  const previewText = panel.previewText || "Live Dashboard Preview";

  const badge = config.floatingBadge || {};
  const badgeTitle = badge.title || "AI-Powered";
  const badgeSubtitle = badge.subtitle || "Insights Engine";
  const badgeTitleColor = badge.titleColor || "#2563eb";
  const badgeSubtitleColor = badge.subtitleColor || "#4b5563";
  const badgeBg = badge.bg || "#ffffff";

  return (
    <section
      dir={config.direction || "ltr"}
      style={bg.style}
      className={`py-14 px-4 ${bg.className}`}
    >
      <div className="container mx-auto max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* LEFT SIDE */}
          <div>
            <h2
              style={{ color: style?.titleColor || undefined }}
              className={`text-4xl md:text-5xl font-bold text-gray-900 mb-6 ${headerAlignClass}`}
            >
              {config.title || "Enterprise-Grade Features"}
            </h2>

            <p
              style={{ color: style?.subtitleColor || undefined }}
              className={`text-xl text-gray-600 mb-10 ${headerAlignClass}`}
            >
              {config.subtitle || "Everything you need to scale your business efficiently"}
            </p>

            {features.length === 0 ? (
              <div className="p-4 rounded-2xl bg-gray-50 border border-gray-200 text-gray-600">
                No features yet. Add features in the editor.
              </div>
            ) : (
              <div className="space-y-4">
                {features.map((feature, index) => {
                  const box = featureIconBox(feature);
                  const isActive = activeTab === index;

                  return (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setActiveTab(index)}
                      style={{
                          backgroundColor: isActive
                            ? style?.featuresTabActiveBg || undefined
                            : style?.featuresTabInactiveBg || undefined,
                          borderColor: isActive
                            ? style?.featuresTabActiveBorder || undefined
                            : undefined,
                        }}
                        className={`w-full ${headerAlignClass} p-6 rounded-2xl transition-all duration-300 ${
                          isActive
                            ? style?.featuresTabActiveBg || style?.featuresTabActiveBorder
                              ? "border-2 shadow-lg"
                              : "bg-gradient-to-r from-blue-50 to-white border-2 border-blue-200 shadow-lg"
                            : style?.featuresTabInactiveBg
                              ? "border border-transparent hover:opacity-90"
                              : "bg-gray-50 hover:bg-gray-100 border border-transparent"
                        }`}
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-12 h-12 rounded-xl flex items-center justify-center text-white me-4 ${box.className}`}
                          style={box.style}
                        >
                          {getIcon(feature.icon)}
                        </div>

                        <div>
                          <h3
                              style={{ color: style?.featuresTabTitleColor || undefined }}
                              className="text-lg font-bold text-gray-900"
                            >
                            {feature.title || "Feature title"}
                          </h3>
                          <p
                              style={{ color: style?.featuresTabDescriptionColor || undefined }}
                              className="text-gray-600"
                            >
                            {feature.description || "Feature short description"}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* RIGHT SIDE */}
          <div className="relative">
            <div
              className="rounded-3xl p-8"
              style={{
                ...gradientStyle(panelBgFrom, panelBgTo, 135),
                color: panelTextColor,
                textAlign: hasAlign ? align : undefined,
              }}
            >
              <div className="mb-8">
                <div className="flex items-center mb-4">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center me-4 ${
                      activeFeature ? featureIconBox(activeFeature).className : ""
                    }`}
                    style={
                      activeFeature
                        ? featureIconBox(activeFeature).style
                        : gradientStyle("#3b82f6", "#a855f7")
                    }
                  >
                    {getIcon(activeFeature?.icon)}
                  </div>

                  <h3 className="text-2xl font-bold">
                    {activeFeature?.title || "Select a feature"}
                  </h3>
                </div>

                <p className="text-lg" style={{ color: panelMutedColor }}>
                  {activeFeature?.details ||
                    "Add a detailed description for this feature (template 3) in the editor."}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-8">
                {stats.slice(0, 4).map((s, i) => (
                  <div
                    key={i}
                    className="p-4 rounded-xl"
                    style={{ backgroundColor: hexToRgba(statCardHex, statCardOpacity) }}
                  >
                    <div className="text-2xl font-bold">{s?.value || ""}</div>
                    <div style={{ color: panelMutedColor }}>{s?.label || ""}</div>
                  </div>
                ))}
              </div>

              <div
                className="relative h-64 rounded-xl overflow-hidden"
                style={{
                  ...gradientStyle(panel.previewBgFrom || "#1f2937", panel.previewBgTo || "#111827", 90),
                }}
              >
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl mb-2">{previewEmoji}</div>
                    <div className="text-sm" style={{ color: panelMutedColor }}>
                      {previewText}
                    </div>
                  </div>
                </div>
              </div>
            </div>

        <div
  className="absolute bottom-3 end-3 sm:bottom-4 sm:end-4 md:-bottom-6 md:-end-6 p-4 sm:p-5 md:p-6 rounded-2xl shadow-2xl max-w-[calc(100%-1.5rem)] sm:max-w-[calc(100%-2rem)] md:max-w-none"
  style={{
    backgroundColor: style?.featuresFloatingBadgeBg || badgeBg,
  }}
>
  <div
    className="text-2xl sm:text-3xl font-bold"
    style={{ color: style?.featuresFloatingBadgeTitleColor || badgeTitleColor }}
  >
    {badgeTitle}
  </div>
  <div
    className="text-sm sm:text-base"
    style={{ color: style?.featuresFloatingBadgeSubtitleColor || badgeSubtitleColor }}
  >
    {badgeSubtitle}
  </div>
</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection3;